import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { storesApi, integrationsApi, CreateStorePayload, POSIntegration, PlatformVerification } from '../api/client';
import clsx from 'clsx';
import {
  BuildingStorefrontIcon,
  PlusIcon,
  Cog6ToothIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { POSStatusBadge, ConnectPOSPrompt, VerificationBadge } from '../components/integrations/POSStatusBadge';
import { ConnectPOSModal } from '../components/integrations/ConnectPOSModal';
import { VerifyOwnershipModal } from '../components/integrations/VerifyOwnershipModal';

// TypeScript interfaces for proper typing
interface StoreStats {
  order_count: number;
  refund_count: number;
  dispute_count: number;
  recovered: number;
}

interface Store {
  id: string;
  name: string;
  external_id?: string;
  aggregator: Aggregator;
  config?: StoreConfig;
  is_active: boolean;
  stats?: StoreStats;
  created_at: string;
}

interface StoreConfig {
  thresholds?: {
    bucket1_item_rate?: number;
    bucket2_value_rate?: number;
    bucket3_min_order?: number;
  };
}

type Aggregator = 'ubereats' | 'doordash' | 'grubhub';

const AGGREGATOR_CONFIG: Record<
  Aggregator,
  { label: string; color: string; bgLight: string; textColor: string }
> = {
  ubereats: {
    label: 'Uber Eats',
    color: '#06C167',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-700',
  },
  doordash: {
    label: 'DoorDash',
    color: '#FF3008',
    bgLight: 'bg-red-50',
    textColor: 'text-red-700',
  },
  grubhub: {
    label: 'Grubhub',
    color: '#F63440',
    bgLight: 'bg-orange-50',
    textColor: 'text-orange-700',
  },
};

export default function Stores() {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [configureStore, setConfigureStore] = useState<Store | null>(null);
  const [newStore, setNewStore] = useState({
    name: '',
    external_id: '',
    aggregator: '' as Aggregator | '',
  });
  const [storeConfig, setStoreConfig] = useState<StoreConfig>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // POS and Verification state
  const [posIntegrations, setPosIntegrations] = useState<POSIntegration[]>([]);
  const [verifications, setVerifications] = useState<PlatformVerification[]>([]);
  const [connectPOSStore, setConnectPOSStore] = useState<Store | null>(null);
  const [verifyStore, setVerifyStore] = useState<Store | null>(null);

  const fetchStores = useCallback(async () => {
    try {
      const res = await storesApi.list();
      setStores(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch stores:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchIntegrations = useCallback(async () => {
    try {
      const [posRes, verRes] = await Promise.all([
        integrationsApi.listPOS(),
        integrationsApi.listVerifications(),
      ]);
      setPosIntegrations(posRes.data.data || []);
      setVerifications(verRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch integrations:', err);
    }
  }, []);

  useEffect(() => {
    fetchStores();
    fetchIntegrations();
  }, [fetchStores, fetchIntegrations]);

  // Helper functions to get POS/verification status for a store
  function getStorePOS(storeId: string): POSIntegration | undefined {
    return posIntegrations.find(p => p.store_id === storeId && p.status === 'connected');
  }

  function getStoreVerification(storeId: string, aggregator: string): PlatformVerification | undefined {
    return verifications.find(v => v.store_id === storeId && v.aggregator === aggregator && v.verification_status === 'verified');
  }

  function handlePOSConnectSuccess() {
    fetchIntegrations();
    setConnectPOSStore(null);
  }

  function handleVerifySuccess() {
    fetchIntegrations();
    setVerifyStore(null);
  }

  async function handleAddStore() {
    if (!newStore.name || !newStore.aggregator) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await storesApi.create({
        name: newStore.name,
        external_id: newStore.external_id || undefined,
        aggregator: newStore.aggregator as CreateStorePayload['aggregator'],
      });
      setShowAddModal(false);
      setNewStore({ name: '', external_id: '', aggregator: '' });
      fetchStores();
    } catch (err) {
      console.error('Failed to add store:', err);
      setError('Failed to add store. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function closeModal() {
    setShowAddModal(false);
    setNewStore({ name: '', external_id: '', aggregator: '' });
    setError('');
  }

  function openConfigureModal(store: Store) {
    setConfigureStore(store);
    setStoreConfig(store.config || { thresholds: {} });
  }

  async function handleSaveConfig() {
    if (!configureStore) return;

    setSaving(true);
    setError('');

    try {
      await storesApi.update(configureStore.id, { config: storeConfig });
      setConfigureStore(null);
      fetchStores();
    } catch (err) {
      console.error('Failed to save configuration:', err);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading stores...</p>
        </div>
      </div>
    );
  }

  const activeStores = stores.filter((s) => s.is_active).length;
  const totalOrders = stores.reduce((sum, s) => sum + (s.stats?.order_count || 0), 0);
  const totalRecovered = stores.reduce((sum, s) => sum + (s.stats?.recovered || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Stores</h1>
          <p className="text-slate-500 mt-1">Manage your connected restaurant locations</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-amber-700 hover:to-amber-600 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all"
        >
          <PlusIcon className="w-4 h-4" />
          Add Store
        </button>
      </div>

      {/* Summary Stats */}
      {stores.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <BuildingStorefrontIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Active Stores</p>
              <p className="text-xl font-bold text-slate-900">
                {activeStores} <span className="text-sm font-normal text-slate-400">/ {stores.length}</span>
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
              <ShoppingCartIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Orders</p>
              <p className="text-xl font-bold text-slate-900">{totalOrders.toLocaleString()}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CheckCircleIcon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Recovered</p>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(totalRecovered)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stores Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stores.map((store) => (
          <StoreCard
            key={store.id}
            store={store}
            formatCurrency={formatCurrency}
            onConfigure={() => openConfigureModal(store)}
            posIntegration={getStorePOS(store.id)}
            isVerified={!!getStoreVerification(store.id, store.aggregator)}
            onConnectPOS={() => setConnectPOSStore(store)}
            onVerify={() => setVerifyStore(store)}
          />
        ))}

        {stores.length === 0 && (
          <div className="col-span-full">
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <BuildingStorefrontIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No stores yet</h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                Connect your first store to start tracking refunds and recovering revenue.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-amber-700 hover:to-amber-600 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all"
              >
                <PlusIcon className="w-4 h-4" />
                Add Your First Store
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Store Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Add New Store</h3>
              <button
                onClick={closeModal}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Store Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newStore.name}
                  onChange={(e) => setNewStore({ ...newStore, name: e.target.value })}
                  placeholder="e.g., Downtown Location"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Platform <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.entries(AGGREGATOR_CONFIG) as [Aggregator, typeof AGGREGATOR_CONFIG[Aggregator]][]).map(
                    ([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setNewStore({ ...newStore, aggregator: key })}
                        className={clsx(
                          'p-3 rounded-xl border-2 text-center transition-all',
                          newStore.aggregator === key
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-slate-200 hover:border-slate-300'
                        )}
                      >
                        <div
                          className={clsx(
                            'text-sm font-medium',
                            newStore.aggregator === key ? 'text-amber-700' : 'text-slate-700'
                          )}
                        >
                          {config.label}
                        </div>
                      </button>
                    )
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Platform Store ID <span className="text-slate-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={newStore.external_id}
                  onChange={(e) => setNewStore({ ...newStore, external_id: e.target.value })}
                  placeholder="e.g., UE-12345"
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Your store ID from the platform's merchant portal
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={closeModal}
                  className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddStore}
                  disabled={saving}
                  className="flex-1 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-amber-700 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Adding...
                    </span>
                  ) : (
                    'Add Store'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configure Store Modal */}
      {configureStore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Configure Store</h3>
                <p className="text-sm text-slate-500">{configureStore.name}</p>
              </div>
              <button
                onClick={() => setConfigureStore(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Dispute Thresholds */}
              <div>
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Dispute Thresholds</h4>
                <p className="text-xs text-slate-500 mb-4">
                  Customize when disputes are automatically flagged for this store.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between text-sm text-slate-700 mb-2">
                      <span>B1: Item Missing Rate</span>
                      <span className="text-amber-600 font-medium text-xs">
                        {storeConfig.thresholds?.bucket1_item_rate || 50}%
                      </span>
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="80"
                      value={storeConfig.thresholds?.bucket1_item_rate || 50}
                      onChange={(e) => setStoreConfig({
                        ...storeConfig,
                        thresholds: { ...storeConfig.thresholds, bucket1_item_rate: parseInt(e.target.value) }
                      })}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Flag when more than this % of items are marked missing
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center justify-between text-sm text-slate-700 mb-2">
                      <span>B2: High Value Refund Rate</span>
                      <span className="text-amber-600 font-medium text-xs">
                        {storeConfig.thresholds?.bucket2_value_rate || 50}%
                      </span>
                    </label>
                    <input
                      type="range"
                      min="30"
                      max="90"
                      value={storeConfig.thresholds?.bucket2_value_rate || 50}
                      onChange={(e) => setStoreConfig({
                        ...storeConfig,
                        thresholds: { ...storeConfig.thresholds, bucket2_value_rate: parseInt(e.target.value) }
                      })}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Flag when refund exceeds this % of order value
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center justify-between text-sm text-slate-700 mb-2">
                      <span>B3: Minimum Order Value</span>
                      <span className="text-amber-600 font-medium text-xs">
                        ${storeConfig.thresholds?.bucket3_min_order || 20}
                      </span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="50"
                      value={storeConfig.thresholds?.bucket3_min_order || 20}
                      onChange={(e) => setStoreConfig({
                        ...storeConfig,
                        thresholds: { ...storeConfig.thresholds, bucket3_min_order: parseInt(e.target.value) }
                      })}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Only flag full refunds on orders above this amount
                    </p>
                  </div>
                </div>
              </div>

              {/* Data Connection */}
              <div>
                <h4 className="text-sm font-semibold text-slate-900 mb-3">Data Connection</h4>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-700">Platform</span>
                    <span className={clsx(
                      'text-xs font-medium px-2 py-0.5 rounded',
                      AGGREGATOR_CONFIG[configureStore.aggregator]?.bgLight,
                      AGGREGATOR_CONFIG[configureStore.aggregator]?.textColor
                    )}>
                      {AGGREGATOR_CONFIG[configureStore.aggregator]?.label || configureStore.aggregator}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-700">Store ID</span>
                    <span className="text-xs font-mono text-slate-500">
                      {configureStore.external_id || 'Not set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-700">Status</span>
                    <span className={clsx(
                      'text-xs font-medium',
                      configureStore.is_active ? 'text-emerald-600' : 'text-slate-500'
                    )}>
                      {configureStore.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Currently using CSV import. API integration coming soon!
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setConfigureStore(null);
                    setError('');
                  }}
                  className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="flex-1 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-amber-700 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/30"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connect POS Modal */}
      {connectPOSStore && (
        <ConnectPOSModal
          storeId={connectPOSStore.id}
          storeName={connectPOSStore.name}
          isOpen={true}
          onClose={() => setConnectPOSStore(null)}
          onSuccess={handlePOSConnectSuccess}
        />
      )}

      {/* Verify Ownership Modal */}
      {verifyStore && (
        <VerifyOwnershipModal
          storeId={verifyStore.id}
          storeName={verifyStore.name}
          aggregator={verifyStore.aggregator}
          hasPOSConnected={!!getStorePOS(verifyStore.id)}
          isOpen={true}
          onClose={() => setVerifyStore(null)}
          onSuccess={handleVerifySuccess}
        />
      )}
    </div>
  );
}

interface StoreCardProps {
  store: Store;
  formatCurrency: (value: number) => string;
  onConfigure: () => void;
  posIntegration?: POSIntegration;
  isVerified: boolean;
  onConnectPOS: () => void;
  onVerify: () => void;
}

function StoreCard({ store, formatCurrency, onConfigure, posIntegration, isVerified, onConnectPOS, onVerify }: StoreCardProps) {
  const aggregator = AGGREGATOR_CONFIG[store.aggregator] || {
    label: store.aggregator,
    bgLight: 'bg-slate-50',
    textColor: 'text-slate-700',
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* Card Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <BuildingStorefrontIcon className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{store.name}</h3>
              {store.external_id && (
                <p className="text-xs text-slate-400 font-mono">ID: {store.external_id}</p>
              )}
            </div>
          </div>
          <StatusIndicator active={store.is_active} />
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <span
            className={clsx(
              'inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium',
              aggregator.bgLight,
              aggregator.textColor
            )}
          >
            {aggregator.label}
          </span>
          {posIntegration && (
            <POSStatusBadge
              status={posIntegration.status as 'connected' | 'pending' | 'error' | 'disconnected'}
              posName={posIntegration.pos_name}
              lastSyncAt={posIntegration.last_sync_at}
              compact
            />
          )}
          <VerificationBadge
            verified={isVerified}
            platform={aggregator.label}
            onVerify={onVerify}
          />
        </div>
      </div>

      {/* Connect POS Prompt - show if no POS connected */}
      {!posIntegration && (
        <div className="px-6 pb-4">
          <ConnectPOSPrompt onConnect={onConnectPOS} />
        </div>
      )}

      {/* Stats Section */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
        <dl className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <dt className="text-xs text-slate-500 mb-1">Orders</dt>
            <dd className="text-lg font-semibold text-slate-900">
              {store.stats?.order_count?.toLocaleString() || 0}
            </dd>
          </div>
          <div className="text-center">
            <dt className="text-xs text-slate-500 mb-1">Refunds</dt>
            <dd className="text-lg font-semibold text-red-600">
              {store.stats?.refund_count?.toLocaleString() || 0}
            </dd>
          </div>
          <div className="text-center">
            <dt className="text-xs text-slate-500 mb-1">Recovered</dt>
            <dd className="text-lg font-semibold text-emerald-600">
              {formatCurrency(store.stats?.recovered || 0)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Card Actions */}
      <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
        <button
          onClick={onConfigure}
          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-colors min-h-[44px]"
        >
          <Cog6ToothIcon className="w-4 h-4" />
          Configure
        </button>
        <Link
          to={`/orders?store=${store.id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 bg-amber-50 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-100 transition-colors min-h-[44px]"
        >
          View Orders
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function StatusIndicator({ active }: { active: boolean }) {
  return (
    <div
      className={clsx(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium',
        active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
      )}
    >
      {active ? (
        <>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          Active
        </>
      ) : (
        <>
          <XCircleIcon className="w-3.5 h-3.5" />
          Inactive
        </>
      )}
    </div>
  );
}
